import api from './api';

/**
 * Service for handling admin-related API requests
 */
class AdminService {
  /**
   * Check if system setup is required
   * @returns {Promise} - The system status response
   */
  async checkSystemStatus() {
    try {
      const response = await api.get('/system/status');
      return response.data;
    } catch (error) {
      console.error('Failed to check system status:', error);
      throw error;
    }
  }

  /**
   * Perform the initial system setup with super admin account
   * @param {Object} setupData - The super admin and system setup data
   * @returns {Promise} - The setup response
   */
  async setupSuperAdmin(setupData) {
    try {
      // Create a clean API instance without interceptors for initial setup
      const setupApi = api.create({
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      const response = await setupApi.post('/admin/setup', setupData);
      
      // Store auth token if it was returned
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('isAdmin', 'true');
      }
      
      return response;
    } catch (error) {
      console.error('Failed to setup super admin:', error);
      throw error;
    }
  }

  /**
   * Get a list of all tenants in the system
   * @returns {Promise} - List of all tenants
   */
  async getAllTenants() {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error("No authentication token found in localStorage");
        throw new Error('Authentication token is missing');
      }
      
      console.log("Using auth token:", token.substring(0, 15) + "...");
      
      // Use a clean api instance without tenant interceptors
      const adminApi = api.create({
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Log the request details for debugging
      console.log(`Making admin request to: /admin/tenants`);
      console.log(`Headers:`, {
        'Authorization': `Bearer ${token.substring(0, 15)}...`,
        'Content-Type': 'application/json'
      });
      
      // Make request without tenant headers to prevent 403 errors
      const response = await adminApi.get('/admin/tenants');
      console.log("Admin tenant response:", response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      
      // Enhanced error logging
      if (error.response) {
        console.error(`Server responded with status: ${error.response.status}`);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error message:', error.message);
      }
      
      throw error;
    }
  }

  /**
   * Approve a tenant registration
   * @param {string} tenantId - ID of the tenant to approve
   * @returns {Promise} - The approval response
   */
  async approveTenant(tenantId) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token is missing');
      
      const adminApi = api.create({
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const response = await adminApi.post(`/admin/tenants/${tenantId}/approve`);
      return response.data;
    } catch (error) {
      console.error(`Failed to approve tenant ${tenantId}:`, error);
      throw error;
    }
  }

  /**
   * Suspend a tenant
   * @param {string} tenantId - ID of the tenant to suspend
   * @param {string} reason - Reason for suspension
   * @returns {Promise} - The suspension response
   */
  async suspendTenant(tenantId, reason) {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token is missing');
      
      const adminApi = api.create({
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const response = await adminApi.post(`/admin/tenants/${tenantId}/suspend`, { reason });
      return response.data;
    } catch (error) {
      console.error(`Failed to suspend tenant ${tenantId}:`, error);
      throw error;
    }
  }

  /**
   * Reactivate a suspended tenant
   * @param {string} tenantId - ID of the tenant to reactivate
   * @returns {Promise} - The reactivation response
   */
  async reactivateTenant(tenantId) {
    return this.approveTenant(tenantId);
  }

  /**
   * Delete a tenant (permanent action)
   * @param {string} tenantId - ID of the tenant to delete
   * @returns {Promise} - The deletion response
   */
  async deleteTenant(tenantId) {
    try {
      const response = await api.delete(`/admin/tenants/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to delete tenant ${tenantId}:`, error);
      throw error;
    }
  }

  /**
   * Get user statistics across all tenants
   * @returns {Promise} - User statistics data
   */
  async getUserStatistics() {
    try {
      const response = await api.get(`/admin/stats/users`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user statistics:', error);
      throw error;
    }
  }

  /**
   * Get system health metrics
   * @returns {Promise} - System health data
   */
  async getSystemHealth() {
    try {
      const response = await api.get(`/admin/system/health`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch system health:', error);
      throw error;
    }
  }

  /**
   * Check if current user has admin privileges
   * @returns {boolean} - True if user is admin
   */
  isAdmin() {
    return localStorage.getItem('isAdmin') === 'true';
  }

  /**
   * Check if current user is a super admin
   * @returns {Promise<boolean>} - True if user is super admin
   */
  async isSuperAdmin() {
    try {
      const response = await api.get(`/auth/verify-role`, {
        params: { role: 'superadmin' }
      });
      return response.data?.isAuthorized || false;
    } catch (error) {
      console.error('Failed to verify super admin status:', error);
      return false;
    }
  }

  /**
   * Fetch all tenant requests
   * @returns {Promise} - List of tenant requests
   */
  async getAllTenantRequests() {
    try {
      const response = await api.get('/tenant-requests');
      return response.data;
    } catch (error) {
      console.error('Error fetching tenant requests:', error);
      throw error;
    }
  }

  /**
   * Approve a tenant request
   * @param {string} requestId - Tenant request ID
   * @returns {Promise<Object>} - Response from the API
   */
  async approveTenantRequest(requestId) {
    try {
      const response = await api.post(`/tenant-requests/${requestId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving tenant request:', error);
      throw error;
    }
  }

  /**
   * Reject a tenant request
   * @param {string} requestId - Tenant request ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} - Response from the API
   */
  async rejectTenantRequest(requestId, reason) {
    try {
      const response = await api.post(`/tenant-requests/${requestId}/reject`, { reason });
      return response.data;
    } catch (error) {
      console.error('Error rejecting tenant request:', error);
      throw error;
    }
  }

  /**
   * Request more information for a tenant request
   * @param {string} requestId - Tenant request ID
   * @param {string} message - Information request message
   * @returns {Promise<Object>} - Response from the API
   */
  async requestMoreInfo(requestId, message) {
    try {
      const response = await api.post(`/tenant-requests/${requestId}/request-info`, { message });
      return response.data;
    } catch (error) {
      console.error('Error requesting more information:', error);
      throw error;
    }
  }

  /**
   * Get all transactions across all tenants
   * @returns {Promise} - List of all tenant transactions
   */
  async getAllTenantTransactions() {
    const token = localStorage.getItem('token');
    
    try {
      const response = await api.get('/superadmin/transactions/all', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching all tenant transactions:', error);
      throw error;
    }
  }
}

export default new AdminService();